import React from 'react';
import { strings } from '../../resources/strings';
import { Button } from '../../common-components/Button/Button';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { DropdownButton, DropdownButtonColor, IDropdownButtonItem } from '../../common-components/DropdownButton/DropdownButton';
import { SeparationType } from '../../enums/SeparationType';
import { ProfileType } from '../../enums/ProfileType';
import { GlobalPickerPerformType } from '../../enums/GlobalPickerPerformType';
import { FileFormat } from '../../enums/FileFormat';
import { ProfileHelper } from '../../helpers/ProfileHelper';
import { SessionHelper } from '../../helpers/SessionHelper';

const keyOptions = {
  UpdateStateOrder: 'update-state-order',
  UpdateStatePrescription: 'update-state-prescription',
  ListToSeparate: 'list-to-separate',
  ZplDanfePackingLabel: 'zpl-danfe-packing-label',
  PdfDanfePackingLabel: 'pdf-danfe-packing-label',
  ZplSimplePackingLabel: 'zpl-simple-packing-label',
  PdfSimplePackingLabel: 'pdf-simple-packing-label',
  ShippingGuide: 'shipping-guide',
  ShippingGuideV2A5: 'shipping-guide-v2a5',
  TSO: 'tso',
  TSOV2A5: 'tso-v2a5',
  ZplLensCertificateLabel: 'zpl-lens-certificate-label',
  PdfLensCertificateLabel: 'pdf-lens-certificate-label',
  NotifyCustomersToCollectRecipeAndDNPs: 'notify-customers-to-collect-recipe-and-dnps',
  ConfirmSeparationByGlobalPicker: 'confirm-separation-by-global-picker',
  PdfLensSeparationList: 'pdf-lens-separation-list',
  CsvLensSeparationList: 'csv-lens-separation-list',
}

interface IActionsForOrdersSelectionProps {
  separationType: SeparationType;
  listHasAnySelectedOrder: boolean;
  ordersOfBatch?: boolean;
  orderBatchId?: number;
  orderBatchIsClosed?: boolean;
  filtering?: boolean;
  appliedFilters?: number;
  userProfile?: ProfileType;
  createOrderBatch?: () => void;
  addOrderBatch?: () => void;
  completeOrderBatch?: () => void;
  removeOrderBatch?: () => void;
  updateStateOrder?: () => void;
  updateStatePrescription?: () => void;
  updateList?: () => void;
  filterList?: () => void;
  clearFilter?: () => void;
  printListToSeparate?: () => void;
  printLensSeparationList?: (fileFormat: FileFormat) => void;
  printShippingGuide?: (version?: number) => void;
  printTSO?: (version?: number) => void;
  printZplDanfePackingLabel?: () => void;
  printPdfDanfePackingLabel?: () => void;
  printZplSimplePackingLabel?: () => void;
  printPdfSimplePackingLabel?: () => void;
  printZplLensCertificateLabel?: () => void;
  printPdfLensCertificateLabel?: () => void;
  notifyCustomersToCollectRecipeAndDNPs?: () => void;
  updateStateOrderByGlobalPicker?: (globalPickerPerformType: GlobalPickerPerformType) => void;
}

const ActionsForOrdersSelection: React.FC<IActionsForOrdersSelectionProps> = ({
  separationType,
  listHasAnySelectedOrder,
  ordersOfBatch,
  orderBatchId,
  orderBatchIsClosed,
  filtering,
  appliedFilters,
  userProfile,
  createOrderBatch,
  addOrderBatch,
  completeOrderBatch,
  removeOrderBatch,
  updateStateOrder,
  updateStatePrescription,
  updateList,
  filterList,
  clearFilter,
  printListToSeparate,
  printLensSeparationList,
  printShippingGuide,
  printTSO,
  printZplDanfePackingLabel,
  printPdfDanfePackingLabel,
  printZplSimplePackingLabel,
  printPdfSimplePackingLabel,
  printZplLensCertificateLabel,
  printPdfLensCertificateLabel,
  notifyCustomersToCollectRecipeAndDNPs,
  updateStateOrderByGlobalPicker
}) => {

  const {
    toastService
  } = React.useContext(ServicesContext);
  const { session } = useAuth();

  let updateOptions: IDropdownButtonItem[] = [];

  if (SessionHelper.canEvolveOrderStateInBatch(session)) {
    updateOptions = [
      ...updateOptions,
      {
        key: keyOptions.UpdateStateOrder,
        text: strings.StateOfOrder
      }
    ];
  }

  let otherOptions: IDropdownButtonItem[] = [];

  let printOptions = [
    {
      key: keyOptions.ListToSeparate,
      text: strings.ListOfSeparation
    }
  ];

  if (SessionHelper.canPrintLensSeparationList(session)) {
    printOptions = [
      ...printOptions,
      {
        key: keyOptions.PdfLensSeparationList,
        text: `${strings.LensSeparationList} em PDF`
      },
      {
        key: keyOptions.CsvLensSeparationList,
        text: `${strings.LensSeparationList} em CSV`
      }
    ];
  }

  printOptions = [
    ...printOptions,
    {
      key: keyOptions.ShippingGuide,
      text: `${strings.GuideOfShipping} A4 (v1)`
    },
    {
      key: keyOptions.ShippingGuideV2A5,
      text: `${strings.GuideOfShipping} A5 (v2)`
    }
  ];

  if (ProfileHelper.canPrintLabels(userProfile)) {
    printOptions = [
      ...printOptions,
      {
        key: keyOptions.ZplDanfePackingLabel,
        text: strings.LabelsOfPackingDanfeInZpl
      },
      {
        key: keyOptions.PdfDanfePackingLabel,
        text: strings.LabelsOfPackingDanfeInPdf
      },
      {
        key: keyOptions.ZplSimplePackingLabel,
        text: strings.LabelsOfPackingSimpleInZpl
      },
      {
        key: keyOptions.PdfSimplePackingLabel,
        text: strings.LabelsOfPackingSimpleInPdf
      }
    ];
  }

  if (separationType === SeparationType.Prescription) {
    if (SessionHelper.canEvolvePriscriptionStateInBatch(session)) {
      updateOptions = [
        ...updateOptions,
        {
          key: keyOptions.UpdateStatePrescription,
          text: strings.StateOfDegree
        }
      ];
    }

    if (ProfileHelper.canPrintLabels(userProfile)) {
      printOptions = [
        ...printOptions,
        {
          key: keyOptions.ZplLensCertificateLabel,
          text: strings.LabelsOfLensCertificationInZpl
        },
        {
          key: keyOptions.PdfLensCertificateLabel,
          text: strings.LabelsOfLensCertificationInPdf
        }
      ];
    }

    printOptions = [
      ...printOptions,
      {
        key: keyOptions.TSO,
        text: `${strings.TSO} A4 (v1)`
      },
      {
        key: keyOptions.TSOV2A5,
        text: `${strings.TSO} A5 (v2)`
      }
    ];

    otherOptions = [
      ...otherOptions,
      {
        key: keyOptions.NotifyCustomersToCollectRecipeAndDNPs,
        text: strings.NotifyCustomersToCollectRecipeAndDNPs
      }
    ];
  }

  let globalPickerOptions: IDropdownButtonItem[] = [];

  if (userProfile === ProfileType.GlobalPicker && separationType === SeparationType.Prescription) {
    globalPickerOptions = [
      ...globalPickerOptions,
      {
        key: keyOptions.ConfirmSeparationByGlobalPicker,
        text: strings.ConfirmSeparationInBatch
      }
    ];
  }

  return (
    <>
      {appliedFilters ?
        <Button text={strings.ClearFilter} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={clearFilter} />
        : null}

      <Button text={strings.Filter} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={filterList} />

      <Button text={strings.UpdateList} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={updateList} />

      {/* {listHasAnySelectedOrder && !orderBatchId ? 
                <Button text={strings.GenerateBatch} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={createOrderBatch} /> 
            : null } */}

      {/* {orderBatchId && !orderBatchIsClosed && ordersOfBatch ? 
                <Button text={strings.CompleteBatch} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={completeOrderBatch} />
            : null } */}

      {/* {listHasAnySelectedOrder && orderBatchId && !ordersOfBatch ? 
                <Button text={strings.AddToBatch} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={addOrderBatch} />
            : null } */}

      {/* {listHasAnySelectedOrder && ordersOfBatch ? 
                <Button text={strings.RemoveFromBatch} disabled={filtering} marginRight={'3px'} marginLeft={'3px'} minWidth={'140px'} click={removeOrderBatch} />
            : null } */}

      {listHasAnySelectedOrder && updateOptions.length > 0 ?
        <DropdownButton
          text={strings.Alter}
          defaultStyle={true}
          outlineButton={true}
          marginRight={'3px'}
          marginLeft={'3px'}
          minWidth={'140px'}
          buttonColor={DropdownButtonColor.Primary}
          items={updateOptions}
          disabled={filtering}
          clickItem={(key) => handleUpdateButtonClick(key)}
        />
        : null}

      {listHasAnySelectedOrder && globalPickerOptions.length > 0 ?
        <DropdownButton
          text={strings.Perform}
          defaultStyle={true}
          outlineButton={true}
          marginRight={'3px'}
          marginLeft={'3px'}
          minWidth={'140px'}
          buttonColor={DropdownButtonColor.Primary}
          items={globalPickerOptions}
          disabled={filtering}
          clickItem={(key) => handleGlobalPickerUpdateButtonClick(key)}
        />
        : null}

      {listHasAnySelectedOrder ?
        <DropdownButton
          text={strings.Print}
          defaultStyle={true}
          outlineButton={true}
          marginRight={'3px'}
          marginLeft={'3px'}
          minWidth={'140px'}
          buttonColor={DropdownButtonColor.Primary}
          items={printOptions}
          disabled={filtering}
          clickItem={(key) => handlePrintButtonClick(key)}
        />
        : null}

      {listHasAnySelectedOrder && otherOptions.length > 0 && ProfileHelper.profilesThatCanAccessOtherOptions.some(p => p === userProfile) ?
        <DropdownButton
          text={strings.OtherOptions}
          defaultStyle={true}
          outlineButton={true}
          marginRight={'3px'}
          marginLeft={'3px'}
          minWidth={'140px'}
          buttonColor={DropdownButtonColor.Primary}
          items={otherOptions}
          disabled={filtering}
          clickItem={(key) => handleOtherButtonClick(key)}
        />
        : null}
    </>
  );

  function handleUpdateButtonClick(key: any) {
    if (key === keyOptions.UpdateStateOrder && updateStateOrder) {
      updateStateOrder();
      return;
    }

    if (key === keyOptions.UpdateStatePrescription && updateStatePrescription) {
      updateStatePrescription();
      return;
    }
  }

  function handleGlobalPickerUpdateButtonClick(key: any) {
    if (key === keyOptions.ConfirmSeparationByGlobalPicker && updateStateOrderByGlobalPicker) {
      updateStateOrderByGlobalPicker(GlobalPickerPerformType.ConfirmSeparation);
      return;
    }
  }

  function handlePrintButtonClick(key: any) {
    if (key === keyOptions.ListToSeparate && printListToSeparate) {
      printListToSeparate();
      return;
    }

    if (key === keyOptions.PdfLensSeparationList && printLensSeparationList) {
      printLensSeparationList(FileFormat.Pdf);
      return;
    }

    if (key === keyOptions.CsvLensSeparationList && printLensSeparationList) {
      printLensSeparationList(FileFormat.Csv);
      return;
    }

    if (key === keyOptions.ShippingGuide && printShippingGuide) {
      printShippingGuide();
      return;
    }

    if (key === keyOptions.ShippingGuideV2A5 && printShippingGuide) {
      printShippingGuide(2);
      return;
    }

    if (key === keyOptions.TSO && printTSO) {
      printTSO();
      return;
    }

    if (key === keyOptions.TSOV2A5 && printTSO) {
      printTSO(2);
      return;
    }

    if (key === keyOptions.ZplDanfePackingLabel && printZplDanfePackingLabel) {
      printZplDanfePackingLabel();
      return;
    }

    if (key === keyOptions.PdfDanfePackingLabel && printPdfDanfePackingLabel) {
      printPdfDanfePackingLabel();
      return;
    }

    if (key === keyOptions.ZplSimplePackingLabel && printZplSimplePackingLabel) {
      printZplSimplePackingLabel();
      return;
    }

    if (key === keyOptions.PdfSimplePackingLabel && printPdfSimplePackingLabel) {
      printPdfSimplePackingLabel();
      return;
    }

    if (key === keyOptions.ZplLensCertificateLabel && printZplLensCertificateLabel) {
      printZplLensCertificateLabel();
      return;
    }

    if (key === keyOptions.PdfLensCertificateLabel && printPdfLensCertificateLabel) {
      printPdfLensCertificateLabel();
      return;
    }
  }

  function handleOtherButtonClick(key: any) {
    if (key === keyOptions.NotifyCustomersToCollectRecipeAndDNPs && notifyCustomersToCollectRecipeAndDNPs) {
      toastService.info('Esta opção está desabilitada por hora. Estamos trabalhando nisso. Obrigado pela compreensão!');
      //notifyCustomersToCollectRecipeAndDNPs();
      return;
    }
  }
}

export { ActionsForOrdersSelection };